/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { setSkipDenialEffect, type SkipDenial } from '@/playback/skipGuard'
import { showTransportTooltip } from './transportTooltip'
import { getString } from '@/transport/i18n/bundle.generated'
import { fireUpsell } from '@/messaging/dm/upsellFire'

/**
 * Register the transport-domain denial surface (tooltip + DM upsell) into the
 * playback-layer skip guard. Called once at boot: the guards in
 * `@/playback/skipGuard` compute WHY a skip was denied, this maps that to the
 * transport tooltip copy and the matching DM upsell. Kept out of the guard
 * itself so `track/` can call the guard without pulling transport chrome (which
 * would close a `track ↔ transport` bi-reference).
 */
export function registerTransportSkipEffects(): void {
    // Denial copy read at fire time. Ad breaks can't be skipped at all (factual,
    // not an upgrade nudge); the skip-cap / back-nav denials nudge an upgrade.
    // The "1 skip left" soft nudge is owned by SkipLimitTooltip.
    const adNotSkippable$ = getString('dmusic_transport_ad_not_skippable_text')
    const skipLimitReached$ = getString('dmusic_transport_skip_limit_reached_text')
    const previousUnavailable$ = getString('dmusic_transport_previous_unavailable_text')
    const seekBackwardDenied$ = getString('dmusic_transport_seek_backward_denied_text')
    setSkipDenialEffect((denial: SkipDenial) => {
        if (denial.direction === 'forward') {
            // Fork mirrors hornet's `next()`: a spent free-tier skip budget
            // raises the skip-cap upsell; a no-skip / ad denial raises the
            // generic feature-unavailable upsell.
            showTransportTooltip(denial.isAd ? adNotSkippable$.value : skipLimitReached$.value)
            fireUpsell(denial.isSkipCap ? 'skipLimit' : 'featureUnavailable')
            return
        }
        if (denial.direction === 'restart') {
            showTransportTooltip(seekBackwardDenied$.value)
            fireUpsell('featureUnavailable', 'rewind')
            return
        }
        // Back-nav denial has no skip-cap fork — hornet's `back()` raises only
        // FEATURE_UNAVAILABLE with a rewind intent.
        showTransportTooltip(denial.isAd ? adNotSkippable$.value : previousUnavailable$.value)
        fireUpsell('featureUnavailable', 'rewind')
    })
}
